import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { from, Observable, throwError } from 'rxjs';
import { LoginService } from '../login/login.service';
import { Router } from '@angular/router';
import { catchError, switchMap } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable()
export class ResponseInterceptor implements HttpInterceptor {

  private sessionExpiredBannerOpen = false;

  constructor(
    private loginService: LoginService,
    private router: Router,
    private snackBar: MatSnackBar
  ) { }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(catchError(err => {
      // 401 = invalid/expired session. 403 = authenticated but not permitted for this resource.
      if (err.status === 401) {
        // An expired access token (e.g. the device slept past the 1h TTL) 401s here even
        // though the session is still recoverable. Refresh once and retry the original
        // request so an in-flight autosave isn't lost to a logout+redirect; only if the
        // refresh fails -- or the retry itself still 401s -- do we fall back to the
        // session-expired banner (which never clears tokens automatically: only the user
        // pressing "Log in again" triggers the explicit logout).
        return from(this.loginService.refreshSession()).pipe(
          switchMap(refreshed => {
            if (!refreshed) {
              this.showSessionExpiredBanner();
              return throwError(err);
            }
            // AccessTokenInterceptor runs before this one on the way out, so the retry
            // won't pass through it again -- stamp the fresh bearer here.
            const retried = request.clone({
              setHeaders: {
                authorization: `Bearer ${this.loginService.access_token}`,
              },
            });
            // next.handle(retried) goes downstream, not back through this interceptor, so
            // catch a still-failing retry here rather than letting it escape unhandled.
            return next.handle(retried).pipe(
              catchError(retryErr => {
                if (retryErr.status === 401) {
                  this.showSessionExpiredBanner();
                }
                return throwError(retryErr);
              }),
            );
          }),
        );
      }

      return throwError(err);
    }))
  }

  private showSessionExpiredBanner(): void {
    if (this.sessionExpiredBannerOpen || this.router.routerState.snapshot.url.includes('login')) {
      return;
    }
    this.sessionExpiredBannerOpen = true;
    const ref = this.snackBar.open('Your session has expired.', 'Log in again', {
      verticalPosition: 'top',
      horizontalPosition: 'right'
    });
    ref.onAction().subscribe(() => {
      this.loginService.logout();
      // Full page load so all in-memory service state is discarded too.
      let url = this.router.routerState.snapshot.url;
      let redirect = url.includes('login') ? '' : `?redirect_to=${encodeURIComponent(url)}`;
      window.location.assign(`/login${redirect}`);
    });
    ref.afterDismissed().subscribe(() => {
      this.sessionExpiredBannerOpen = false;
    });
  }
}
